import { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CPE, CommercialStatus, COMMERCIAL_STATUSES } from '../types/cpe';
import { cpeService } from '../services/cpeService';
import CPEBoardCard from './CPEBoardCard';

/** Color map for commercial status column headers */
const COLUMN_COLORS: Record<CommercialStatus, string> = {
  Uncontacted: '#607d8b',
  Contacted: '#1976d2',
  'O&M Offered': '#f57c00',
  'O&M Contracted': '#388e3c',
  'Lost O&M': '#c62828',
};

interface DroppableColumnProps {
  status: CommercialStatus;
  cpes: CPE[];
  zoneMap: Record<string, string>;
  onCardClick: (cpe: CPE) => void;
}

/**
 * A single droppable column for one commercial status.
 */
const DroppableColumn: React.FC<DroppableColumnProps> = ({
  status,
  cpes,
  zoneMap,
  onCardClick,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const { t } = useTranslation();

  return (
    <Paper
      sx={{
        flex: '0 0 220px',
        minWidth: 220,
        maxWidth: 240,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isOver ? 'action.hover' : 'background.paper',
        border: '1px solid',
        borderColor: isOver ? COLUMN_COLORS[status] : 'divider',
        borderRadius: 2,
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
      elevation={1}
    >
      {/* Column header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: '2px solid',
          borderBottomColor: COLUMN_COLORS[status],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
          {status}
        </Typography>
        <Chip
          label={cpes.length}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem', bgcolor: COLUMN_COLORS[status], color: '#fff' }}
        />
      </Box>

      {/* Card list */}
      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 1,
          minHeight: 80,
          maxHeight: 'calc(100vh - 280px)',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'action.active' },
        }}
      >
        <SortableContext
          items={cpes.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {cpes.length === 0 ? (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', textAlign: 'center', mt: 2 }}
            >
              {t('cpe.board.noCards', 'No PPs')}
            </Typography>
          ) : (
            cpes.map((cpe) => (
              <CPEBoardCard
                key={cpe.id}
                cpe={cpe}
                zoneMap={zoneMap}
                onClick={onCardClick}
              />
            ))
          )}
        </SortableContext>
      </Box>
    </Paper>
  );
};

interface CPEBoardViewProps {
  /** All CPEs to show on the board */
  cpes: CPE[];
  /** Zone id → name lookup */
  zoneMap: Record<string, string>;
  /** Loading indicator */
  loading: boolean;
  /** Error message, if any */
  error: string;
  /** Called when a card is clicked */
  onCardClick: (cpe: CPE) => void;
  /** Called after an optimistic status update so parent can sync state */
  onStatusChange: (cpeId: string, newStatus: CommercialStatus) => void;
}

/**
 * Jira-style kanban board view for CPEs, grouped by commercial status.
 * Drag-and-drop updates commercial_status optimistically.
 */
export const CPEBoardView: React.FC<CPEBoardViewProps> = ({
  cpes,
  zoneMap,
  loading,
  error,
  onCardClick,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const [activeCPE, setActiveCPE] = useState<CPE | null>(null);
  // Track current column for each cpe by id (optimistic)
  const [optimisticStatus, setOptimisticStatus] = useState<Record<string, CommercialStatus>>({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  /** Merge optimistic overrides into the base cpes list */
  const resolvedCPEs = cpes.map((c) => ({
    ...c,
    commercial_status: optimisticStatus[c.id] ?? c.commercial_status,
  }));

  const byStatus = (status: CommercialStatus) =>
    resolvedCPEs.filter((c) => c.commercial_status === status);

  const handleDragStart = (event: DragStartEvent) => {
    const cpe = cpes.find((c) => c.id === event.active.id);
    setActiveCPE(cpe ?? null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) return;

    const overId = String(over.id);
    const targetStatus = COMMERCIAL_STATUSES.includes(overId as CommercialStatus)
      ? (overId as CommercialStatus)
      : resolvedCPEs.find((c) => c.id === overId)?.commercial_status ?? null;

    if (!targetStatus || !activeCPE) return;
    if ((optimisticStatus[activeCPE.id] ?? activeCPE.commercial_status) === targetStatus) return;

    setOptimisticStatus((prev) => ({ ...prev, [activeCPE.id]: targetStatus }));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCPE(null);

    if (!over) {
      // Dropped outside — revert
      setOptimisticStatus((prev) => {
        const next = { ...prev };
        delete next[String(active.id)];
        return next;
      });
      return;
    }

    const overId = String(over.id);
    const newStatus = COMMERCIAL_STATUSES.includes(overId as CommercialStatus)
      ? (overId as CommercialStatus)
      : resolvedCPEs.find((c) => c.id === overId)?.commercial_status ?? null;

    const cpe = cpes.find((c) => c.id === String(active.id));
    if (!cpe || !newStatus || cpe.commercial_status === newStatus) {
      setOptimisticStatus((prev) => {
        const next = { ...prev };
        delete next[String(active.id)];
        return next;
      });
      return;
    }

    // Notify parent of confirmed optimistic change
    onStatusChange(cpe.id, newStatus);

    // Clear from local optimistic map (parent owns the truth now)
    setOptimisticStatus((prev) => {
      const next = { ...prev };
      delete next[cpe.id];
      return next;
    });

    // Persist to backend
    try {
      await cpeService.updateCPE(cpe.id, { commercial_status: newStatus });
    } catch {
      // Revert parent state
      onStatusChange(cpe.id, cpe.commercial_status as CommercialStatus);
      toast.error(t('cpe.board.updateError', 'Failed to update status. Change reverted.'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {cpes.length >= 3000 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('cpe.board.cap', 'Showing first 3000 results. Refine filters to see more.')}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          pb: 2,
          alignItems: 'flex-start',
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'action.active' },
        }}
      >
        {COMMERCIAL_STATUSES.map((status) => (
          <DroppableColumn
            key={status}
            status={status}
            cpes={byStatus(status)}
            zoneMap={zoneMap}
            onCardClick={onCardClick}
          />
        ))}
      </Box>

      {/* Drag overlay — card preview while dragging */}
      <DragOverlay>
        {activeCPE ? (
          <CPEBoardCard
            cpe={activeCPE}
            zoneMap={zoneMap}
            onClick={() => undefined}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default CPEBoardView;
